import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2, AlertCircle, Pencil, Database } from "lucide-react";

const EMPTY = {
  ticker: "",
  name: "",
  par_value: 25,
  coupon_rate: "",
  payment_frequency: 4,
  maturity_date: "",
  shares: "",
  purchase_price: "",
  purchase_date: new Date().toISOString().slice(0, 10),
  notes: "",
};

export default function HoldingDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // catalog lookup state: 'idle' | 'searching' | 'found' | 'not_found'
  const [lookup, setLookup] = useState({ state: "idle", bond: null });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  // Track which fields were autofilled from catalog (Set of field names)
  const [autofilled, setAutofilled] = useState(new Set());
  // Track which fields are currently flashing (briefly highlighted post-fill)
  const [flashing, setFlashing] = useState(new Set());
  const debounceRef = useRef(null);
  const suggestDebounceRef = useRef(null);
  const reqIdRef = useRef(0);
  const suggestReqIdRef = useRef(0);

  useEffect(() => {
    if (editing) {
      setForm({ ...EMPTY, ...editing });
      setLookup({ state: "idle", bond: null });
      setSuggestions([]);
      setAutofilled(new Set());
      setFlashing(new Set());
    } else {
      setForm(EMPTY);
      setLookup({ state: "idle", bond: null });
      setSuggestions([]);
      setAutofilled(new Set());
      setFlashing(new Set());
    }
  }, [editing, open]);

  // Helper — write catalog data into the form, mark autofilled + flash
  const applyCatalogData = (bond) => {
    const FIELDS = ["name", "par_value", "coupon_rate", "payment_frequency", "maturity_date"];
    const fallbackName = bond.fullname || bond.company_name || `${bond.ticker} ${bond.coupon_rate ?? ""}%`.trim();
    const values = {
      name: fallbackName,
      par_value: bond.par_value ?? 25,
      coupon_rate: bond.coupon_rate ?? "",
      payment_frequency: bond.payment_frequency || 4,
      maturity_date: bond.maturity_date || "",
    };
    setForm((f) => ({ ...f, ...values }));
    const filled = new Set(FIELDS.filter((k) => values[k] !== "" && values[k] !== null && values[k] !== undefined));
    setAutofilled(filled);
    // Flash animation: trigger immediately, clear after 900ms
    setFlashing(new Set(filled));
    setTimeout(() => setFlashing(new Set()), 900);
  };

  // Suggestions (autocomplete dropdown) — separate debounce, 200ms, min 1 char
  useEffect(() => {
    if (editing) return;
    if (suggestDebounceRef.current) clearTimeout(suggestDebounceRef.current);
    const ticker = (form.ticker || "").trim();
    if (!ticker || lookup.state === "found") {
      setSuggestions([]);
      return;
    }
    suggestDebounceRef.current = setTimeout(async () => {
      const myReq = ++suggestReqIdRef.current;
      try {
        const { data } = await api.get(`/catalog/search/${encodeURIComponent(ticker)}`);
        if (myReq !== suggestReqIdRef.current) return;
        setSuggestions(data || []);
      } catch {
        if (myReq === suggestReqIdRef.current) setSuggestions([]);
      }
    }, 200);
    return () => { if (suggestDebounceRef.current) clearTimeout(suggestDebounceRef.current); };
  }, [form.ticker, editing, lookup.state]);

  const applyCatalog = (bond) => {
    setForm((f) => ({ ...f, ticker: bond.ticker }));
    applyCatalogData(bond);
    setLookup({ state: "found", bond });
    setSuggestions([]);
    setShowSuggest(false);
  };

  // Debounced catalog lookup on ticker change (only for new positions)
  useEffect(() => {
    if (editing) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const ticker = (form.ticker || "").trim().toUpperCase();
    if (!ticker || ticker.length < 2) {
      setLookup({ state: "idle", bond: null });
      return;
    }
    setLookup((s) => (s.state === "found" && s.bond?.ticker === ticker ? s : { state: "searching", bond: null }));
    debounceRef.current = setTimeout(async () => {
      const myReq = ++reqIdRef.current;
      try {
        const { data } = await api.get(`/catalog/by-ticker/${encodeURIComponent(ticker)}`);
        if (myReq !== reqIdRef.current) return;  // stale
        setLookup({ state: "found", bond: data });
        applyCatalogData(data);
      } catch (e) {
        if (myReq !== reqIdRef.current) return;
        if (e?.response?.status === 404) {
          setLookup({ state: "not_found", bond: null });
        } else {
          setLookup({ state: "idle", bond: null });
        }
      }
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [form.ticker, editing]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    // If user edits an autofilled field, mark it as user-owned now
    setAutofilled((s) => {
      if (!s.has(k)) return s;
      const next = new Set(s);
      next.delete(k);
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        par_value: Number(form.par_value),
        coupon_rate: Number(form.coupon_rate),
        payment_frequency: Number(form.payment_frequency),
        shares: Number(form.shares),
        purchase_price: Number(form.purchase_price),
      };
      if (editing?.id) {
        await api.put(`/holdings/${editing.id}`, payload);
        toast.success("Holding updated");
      } else {
        await api.post("/holdings", payload);
        toast.success("Holding added");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const inputBase = "w-full px-3 py-2 text-sm font-mono text-graphite focus:outline-none rounded-none transition-all duration-300";
  // USER fields — paars accent, distinct, with a "// YOU FILL" label tag
  const inputUser = `${inputBase} bg-champagne border-2 border-paars/60 focus:border-paars`;
  // AUTOFILLED fields — muted, paars wash, "// FROM CATALOG"
  const inputAuto = `${inputBase} bg-paars/10 border border-paars/30 focus:border-paars`;
  // FLASHING — bright groen pulse for ~900ms after autofill
  const inputFlash = `${inputBase} bg-groen/30 border-2 border-groen-dark animate-pulse`;
  // Default neutral (e.g., before any lookup)
  const inputNeutral = `${inputBase} bg-champagne border border-stone focus:border-paars`;

  const fieldCls = (field, opts = {}) => {
    if (flashing.has(field)) return inputFlash;
    if (opts.userField) return inputUser;
    if (autofilled.has(field)) return inputAuto;
    return inputNeutral;
  };

  const labelCls = (field, opts = {}) => {
    const base = "text-[10px] tracking-[0.2em] uppercase font-bold flex items-center gap-1.5 mb-1.5";
    if (opts.userField) return `${base} text-paars-dark`;
    if (autofilled.has(field)) return `${base} text-graphite/45`;
    return `${base} text-graphite/55`;
  };

  const FieldBadge = ({ field, isUser }) => {
    if (isUser) return <span className="text-[8px] tracking-[0.3em] uppercase bg-paars text-graphite px-1.5 py-0.5 ml-1">YOU FILL</span>;
    if (autofilled.has(field)) return <span className="text-[8px] tracking-[0.3em] uppercase bg-stone/80 text-graphite/60 px-1.5 py-0.5 ml-1">FROM CATALOG</span>;
    return null;
  };

  const matched = !editing && lookup.state === "found";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!rounded-none bg-white border border-stone max-w-2xl text-graphite p-0">
        <DialogHeader className="px-6 py-4 border-b border-stone">
          <DialogTitle className="text-base font-mono uppercase tracking-widest text-paars-dark">
            {editing ? "// EDIT POSITION" : "// NEW POSITION"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="p-6 space-y-5" data-testid="holding-form">
          {/* SECTION 1 — Transaction (YOU FILL) */}
          <div className="border-l-2 border-paars pl-4 space-y-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold flex items-center gap-2">
              <Pencil className="w-3 h-3" strokeWidth={2.5} />
              // YOU FILL THESE
            </div>
            <div>
              <label className={labelCls("ticker", { userField: true })}>
                Ticker * <FieldBadge field="ticker" isUser />
              </label>
              <div className="relative">
                <input
                  data-testid="ticker-input"
                  required
                  value={form.ticker}
                  onChange={(e)=>set("ticker", e.target.value.toUpperCase())}
                  onFocus={()=>setShowSuggest(true)}
                  onBlur={()=>setTimeout(()=>setShowSuggest(false), 180)}
                  className={`${fieldCls("ticker", { userField: true })} pr-9 text-base tracking-widest`}
                  placeholder="Type ticker — e.g. RWTN"
                  autoComplete="off"
                />
                {!editing && (
                  <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                    {lookup.state === "searching" && <Loader2 className="w-4 h-4 text-graphite/45 animate-spin" />}
                    {lookup.state === "found" && <CheckCircle2 className="w-4 h-4 text-groen-dark" />}
                    {lookup.state === "not_found" && <AlertCircle className="w-4 h-4 text-gold" />}
                  </div>
                )}
                {!editing && showSuggest && suggestions.length > 0 && (
                  <div data-testid="ticker-suggestions" className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-stone shadow-xl max-h-72 overflow-y-auto">
                    {suggestions.map((s) => (
                      <button
                        type="button"
                        key={s.id}
                        data-testid={`suggest-${s.ticker}`}
                        onMouseDown={(e)=>{ e.preventDefault(); applyCatalog(s); }}
                        className="w-full text-left px-3 py-2 border-b border-stone/60 hover:bg-paars/15 flex items-baseline justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="font-mono font-bold text-graphite text-sm tracking-wider">{s.ticker}</div>
                          <div className="text-[11px] text-graphite/55 truncate">{s.fullname || s.company_name || "—"}</div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="font-mono text-xs text-paars-dark">{Number(s.coupon_rate).toFixed(3)}%</div>
                          <div className="text-[10px] text-graphite/45 font-mono">{s.maturity_date}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelCls("shares", { userField: true })}>
                  Shares * <FieldBadge field="shares" isUser />
                </label>
                <input data-testid="shares-input" required type="number" step="0.0001" value={form.shares} onChange={(e)=>set("shares", e.target.value)} className={fieldCls("shares", { userField: true })} placeholder="100" />
              </div>
              <div>
                <label className={labelCls("purchase_price", { userField: true })}>
                  Price * <FieldBadge field="purchase_price" isUser />
                </label>
                <input data-testid="price-input" required type="number" step="0.0001" value={form.purchase_price} onChange={(e)=>set("purchase_price", e.target.value)} className={fieldCls("purchase_price", { userField: true })} placeholder="24.50" />
              </div>
              <div>
                <label className={labelCls("purchase_date", { userField: true })}>
                  Buy Date * <FieldBadge field="purchase_date" isUser />
                </label>
                <input data-testid="purchase-date-input" required type="date" value={form.purchase_date} onChange={(e)=>set("purchase_date", e.target.value)} className={fieldCls("purchase_date", { userField: true })} />
              </div>
            </div>
          </div>

          {/* Catalog match banner */}
          {!editing && lookup.state === "found" && (
            <div data-testid="catalog-match-banner" className="flex items-center justify-between gap-3 border border-groen-dark/40 bg-groen/15 px-4 py-2.5">
              <div className="text-xs text-graphite">
                <span className="font-bold text-groen-dark tracking-wider uppercase">✓ Catalog match</span>
                <span className="ml-2 text-graphite/70">
                  {lookup.bond.fullname || lookup.bond.company_name || lookup.bond.ticker} — fixed fields below auto-filled.
                </span>
              </div>
              <button
                type="button"
                data-testid="catalog-clear-btn"
                onClick={() => { setForm(EMPTY); setLookup({ state: "idle", bond: null }); setAutofilled(new Set()); }}
                className="text-[10px] tracking-widest uppercase text-graphite/55 hover:text-burgundy"
              >
                Clear
              </button>
            </div>
          )}
          {!editing && lookup.state === "not_found" && form.ticker.length >= 2 && (
            <div data-testid="catalog-no-match-banner" className="flex items-center gap-2 border border-gold/40 bg-gold/10 px-4 py-2.5 text-xs text-graphite">
              <AlertCircle className="w-3.5 h-3.5 text-gold flex-shrink-0" />
              <span><span className="font-bold tracking-wider uppercase">Not in catalog</span> — fill in the bond fields below manually.</span>
            </div>
          )}

          {/* SECTION 2 — Bond static data (FROM CATALOG when matched) */}
          <div className="border-l-2 border-stone-warm pl-4 space-y-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-graphite/45 font-bold flex items-center gap-2">
              <Database className="w-3 h-3" strokeWidth={2.5} />
              // BOND SPECIFICS {matched && <span className="text-groen-dark">· AUTO-FILLED</span>}
            </div>
            <div>
              <label className={labelCls("name")}>
                Issuer Name <FieldBadge field="name" />
              </label>
              <input value={form.name} onChange={(e)=>set("name", e.target.value)} className={fieldCls("name")} placeholder="Atlanticus Holdings 6.125%" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelCls("par_value")}>
                  Par Value * <FieldBadge field="par_value" />
                </label>
                <input data-testid="par-input" required type="number" step="0.01" value={form.par_value} onChange={(e)=>set("par_value", e.target.value)} className={fieldCls("par_value")} />
              </div>
              <div>
                <label className={labelCls("coupon_rate")}>
                  Coupon % * <FieldBadge field="coupon_rate" />
                </label>
                <input data-testid="coupon-input" required type="number" step="0.001" value={form.coupon_rate} onChange={(e)=>set("coupon_rate", e.target.value)} className={fieldCls("coupon_rate")} placeholder="6.125" />
              </div>
              <div>
                <label className={labelCls("payment_frequency")}>
                  Payments/yr * <FieldBadge field="payment_frequency" />
                </label>
                <select data-testid="freq-input" required value={form.payment_frequency} onChange={(e)=>set("payment_frequency", e.target.value)} className={fieldCls("payment_frequency")}>
                  <option value={1}>1 (Annual)</option>
                  <option value={2}>2 (Semi)</option>
                  <option value={4}>4 (Quarterly)</option>
                  <option value={12}>12 (Monthly)</option>
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls("maturity_date")}>
                Maturity Date * <FieldBadge field="maturity_date" />
              </label>
              <input data-testid="maturity-input" required type="date" value={form.maturity_date} onChange={(e)=>set("maturity_date", e.target.value)} className={fieldCls("maturity_date")} />
            </div>
          </div>

          <div>
            <label className={labelCls("notes")}>Notes</label>
            <textarea value={form.notes} onChange={(e)=>set("notes", e.target.value)} className={inputNeutral} rows={2} placeholder="Optional notes about this position" />
          </div>

          <DialogFooter className="!flex !justify-end gap-2 pt-2">
            <button type="button" onClick={()=>onOpenChange(false)} className="px-5 py-2.5 border border-paars-dark/30 text-graphite/80 text-sm hover:bg-stone/50">
              Cancel
            </button>
            <button data-testid="save-holding-btn" type="submit" disabled={saving} className="px-5 py-2.5 bg-paars text-graphite font-semibold text-sm hover:bg-paars-light disabled:opacity-50">
              {saving ? "SAVING..." : editing ? "UPDATE POSITION" : "ADD POSITION"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
